import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, ChevronRight, Newspaper } from "lucide-react";

const INTERVIEW_ARTICLE = "/articolo/intervista-paolo-ragazzini-coppa-romagna-slalom-sogliano";
const FORMIA_ARTICLE = "/articolo/formia-ability-show-coppa-sportiva-nazionale-automobilismo-2026";
const VERUCCHIO_ARTICLE = "/articolo/rast-raduno-auto-storiche-tedesche-verucchio-7-novembre-2026";

const VER_START = new Date("2026-11-07T20:30:00+01:00").getTime();
const VER_END = new Date("2026-11-08T00:00:00+01:00").getTime();
const FORMIA_STEPS = [
  { at: new Date("2026-11-14T09:00:00+01:00").getTime(), label: "Mancano all'apertura del Motor Village · sab 9:00" },
  { at: new Date("2026-11-15T10:00:00+01:00").getTime(), label: "Mancano alla partenza della 1ª manche · dom 10:00" },
];
const FORMIA_END = new Date("2026-11-15T18:00:00+01:00").getTime();

// ?cd_now=2026-11-10T12:00:00%2B01:00 lets the editors preview future states of the strip
const OFFSET = (() => {
  const q = new URLSearchParams(window.location.search).get("cd_now");
  const t = q ? Date.parse(q) : NaN;
  return Number.isNaN(t) ? 0 : t - Date.now();
})();
const now = () => Date.now() + OFFSET;

function split(ms) {
  return { d: Math.floor(ms / 86400000), h: Math.floor((ms % 86400000) / 3600000), m: Math.floor((ms % 3600000) / 60000), s: Math.floor((ms % 60000) / 1000) };
}

const Cell = ({ v, label }) => (
  <div className="flex flex-col items-center">
    <div className="relative overflow-hidden rounded-sm bg-white/10 backdrop-blur px-2 sm:px-2.5 py-1 min-w-[40px] sm:min-w-[48px]">
      <span key={v} className="block font-display font-black text-xl sm:text-2xl tabular-nums text-white text-center animate-fade-up">{String(v).padStart(2, "0")}</span>
    </div>
    <span className="text-[9px] font-mono uppercase tracking-widest text-white/70 mt-0.5">{label}</span>
  </div>
);

const Timer = ({ ms, testid }) => {
  const t = split(ms);
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      <Cell v={t.d} label="giorni" /><Cell v={t.h} label="ore" /><Cell v={t.m} label="min" /><Cell v={t.s} label="sec" />
    </div>
  );
};

const Badge = ({ children, testid }) => (
  <span data-testid={testid} className="ml-1 align-middle text-[10px] font-mono tracking-widest bg-legend-yellow text-legend-ink px-1.5 py-0.5 rounded-sm">{children}</span>
);

const SmallBtn = ({ to, testid, children }) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center gap-1 bg-white text-legend-red hover:bg-legend-yellow hover:text-legend-ink text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-sm transition-colors">
    {children} <ChevronRight size={12} />
  </Link>
);

function Verucchio({ n }) {
  const badge = n < VER_START ? "A breve" : "In corso";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="upcoming-verucchio">
      <div>
        <p className="font-display font-black uppercase text-lg leading-tight">R.A.S.T. Verucchio · 7 Nov <Badge testid="verucchio-badge">{badge}</Badge></p>
        <div className="mt-1"><SmallBtn to={VERUCCHIO_ARTICLE} testid="upcoming-verucchio-article">Leggi l'articolo</SmallBtn></div>
      </div>
      {n < VER_START && <Timer ms={VER_START - n} testid="verucchio-countdown" />}
    </div>
  );
}

function Formia({ n }) {
  const step = FORMIA_STEPS.find((s) => n < s.at);
  const badge = step ? (step === FORMIA_STEPS[0] ? "Prossimo" : "Weekend in corso") : n < FORMIA_END ? "In gara" : "Concluso";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="upcoming-formia">
      <div>
        <p className="font-display font-black uppercase text-lg leading-tight">Ability Show Formia (LT) · 14-15 Nov <Badge testid="formia-badge">{badge}</Badge></p>
        {step && <p className="text-[10px] font-mono uppercase tracking-widest text-white/80 mt-0.5" data-testid="formia-countdown-label">{step.label}</p>}
        <div className="mt-1"><SmallBtn to={FORMIA_ARTICLE} testid="upcoming-formia-article">Leggi l'articolo</SmallBtn></div>
      </div>
      {step && <Timer ms={step.at - n} testid="formia-countdown" />}
    </div>
  );
}

function SmallRow({ label, badge, to, testid, btnTestid }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 border-t border-white/20" data-testid={testid}>
      <span className="font-display font-bold uppercase text-sm sm:text-base leading-tight text-white/90">{label}{badge && <Badge>{badge}</Badge>}</span>
      <SmallBtn to={to} testid={btnTestid}>Leggi l'articolo</SmallBtn>
    </div>
  );
}

export default function Countdown() {
  const [n, setN] = useState(now());
  useEffect(() => {
    const id = setInterval(() => setN(now()), 1000);
    return () => clearInterval(id);
  }, []);
  const verucchioDone = n >= VER_END;

  return (
    <div data-testid="sogliano-countdown" className="bg-legend-red text-white relative overflow-hidden">
      <div className="checkered absolute inset-y-0 right-0 w-40 opacity-10" />
      <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 relative">
        <div className="flex items-start gap-2 w-full sm:w-auto" data-testid="legend-upcoming-events">
          <Flag className="text-legend-yellow shrink-0 mt-0.5" size={20} />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-mono uppercase text-[10px] tracking-[0.25em] text-legend-yellow">Prossimi eventi con Legend Magazine presente</p>
            {verucchioDone ? (
              <>
                <Formia n={n} />
                <SmallRow label="R.A.S.T. Verucchio · 7 Nov" badge="Concluso" to={VERUCCHIO_ARTICLE} testid="verucchio-done" btnTestid="upcoming-verucchio-article" />
              </>
            ) : (
              <>
                <Verucchio n={n} />
                <SmallRow label="Ability Show Formia (LT) · 14-15 Nov" to={FORMIA_ARTICLE} testid="upcoming-formia" btnTestid="upcoming-formia-article" />
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center sm:items-end gap-1">
          <Link to={INTERVIEW_ARTICLE} data-testid="sogliano-interview-link" className="inline-flex items-center gap-2 bg-legend-ink hover:bg-black text-white text-xs sm:text-sm font-bold uppercase tracking-wide px-3 py-2 rounded-sm transition-colors">
            <Newspaper size={15} className="text-legend-yellow" /> Intervista a Paolo Ragazzini
          </Link>
          <span className="text-[9px] font-mono uppercase tracking-widest text-white/70">Intervista di Davide Bolognesi</span>
        </div>
      </div>
    </div>
  );
}
